import { useState, useEffect } from "react";
import { authAxios } from "../../utils/auth";
import { Brain, Zap, FileText, Timer, RefreshCw } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const GOLD = "#C5A55A";
const CHART_COLORS = {
  timeline_only: "#3d8b6b",
  claim_only: "#6b3d5e",
  both_blocks: GOLD,
  dropped_by_filter: "#8b6b3d",
  empty_recall: "#5e3d6b",
  no_recall: "#3d5e8b",
};

function pct(n) {
  return `${(n * 100).toFixed(1)}%`;
}

function fmtChars(n) {
  if (!n) return "0";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function StatTile({ icon: Icon, label, value, sub, testId }) {
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-white/[0.06] bg-[#111113] p-5 flex flex-col gap-1"
      data-testid={testId}
    >
      <div className="absolute top-0 right-0 w-24 h-24 bg-[#C5A55A]/[0.03] rounded-bl-full" />
      <div className="flex items-center gap-2 text-white/40 text-xs font-medium uppercase tracking-wider mb-1">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <p className="text-3xl font-bold text-white tracking-tight">{value}</p>
      {sub && <p className="text-xs text-white/40 mt-1">{sub}</p>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1e] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-white/60 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-white font-medium">
          <span style={{ color: p.color || GOLD }}>{p.name}: </span>
          {p.value}
        </p>
      ))}
    </div>
  );
}

export default function RecallTab({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await authAxios.get(
        `/analytics/command/recall-stats?range=${range}`,
      );
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  if (loading && !data) {
    return (
      <div className="text-white/40 text-sm p-6" data-testid="recall-tab-loading">
        Loading recall stats…
      </div>
    );
  }

  if (err) {
    return (
      <div className="text-red-400 text-sm p-6" data-testid="recall-tab-error">
        Error: {err}
      </div>
    );
  }

  if (!data || data.hero.total_turns === 0) {
    return (
      <div className="text-white/40 text-sm p-6" data-testid="recall-tab-empty">
        No recall activity in the selected range. Start a coach chat with
        EROSCOACH_RECALL=on to populate this view.
      </div>
    );
  }

  const { hero, breakdown, top_queries, recent_turns } = data;

  const breakdownChart = [
    { name: "No Recall", key: "no_recall", value: breakdown.no_recall },
    { name: "Timeline Only", key: "timeline_only", value: breakdown.timeline_only },
    { name: "Claim Only", key: "claim_only", value: breakdown.claim_only },
    { name: "Both Blocks", key: "both_blocks", value: breakdown.both_blocks },
    { name: "Filter Dropped", key: "dropped_by_filter", value: breakdown.dropped_by_filter },
    { name: "Empty Recall", key: "empty_recall", value: breakdown.empty_recall },
  ];

  return (
    <div className="space-y-6" data-testid="recall-tab">
      {/* ── Hero tiles ─────────────────────────────────────────── */}
      <div className="flex justify-between items-center">
        <h2 className="text-white/80 text-lg font-semibold">ErosCoach Recall</h2>
        <button
          onClick={fetchStats}
          className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition"
          data-testid="recall-refresh-btn"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile
          icon={Brain}
          label="Recall Rate"
          value={pct(hero.recall_rate)}
          sub={`${hero.total_turns} coach turns`}
          testId="recall-tile-recall-rate"
        />
        <StatTile
          icon={Zap}
          label="Injection Rate"
          value={pct(hero.injection_rate)}
          sub="prefix actually shipped"
          testId="recall-tile-injection-rate"
        />
        <StatTile
          icon={FileText}
          label="Avg Prefix"
          value={fmtChars(hero.avg_prefix_chars)}
          sub="chars per injected turn"
          testId="recall-tile-avg-prefix"
        />
        <StatTile
          icon={Timer}
          label="Classifier Latency"
          value={`${hero.avg_classifier_ms}ms`}
          sub={`${pct(hero.cache_hit_rate)} cache hit`}
          testId="recall-tile-latency"
        />
      </div>

      {/* ── Breakdown chart ────────────────────────────────────── */}
      <div className="bg-[#111113] border border-white/[0.06] rounded-xl p-5" data-testid="recall-breakdown-chart">
        <div className="text-white/60 text-xs uppercase tracking-wider mb-4">
          Turn Breakdown
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={breakdownChart}>
            <XAxis dataKey="name" tick={{ fill: "#fff8", fontSize: 11 }} />
            <YAxis tick={{ fill: "#fff8", fontSize: 11 }} allowDecimals={false} />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: "#fff0" }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {breakdownChart.map((entry) => (
                <Cell key={entry.key} fill={CHART_COLORS[entry.key] || GOLD} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ── Top queries + recent turns side by side ────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top queries */}
        <div className="bg-[#111113] border border-white/[0.06] rounded-xl p-5" data-testid="recall-top-queries">
          <div className="text-white/60 text-xs uppercase tracking-wider mb-3">
            Top Search Queries
          </div>
          {top_queries.length === 0 ? (
            <p className="text-white/30 text-sm">No queries yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-white/40 border-b border-white/5">
                  <th className="text-left py-1.5 font-medium">Query</th>
                  <th className="text-right py-1.5 font-medium">Count</th>
                  <th className="text-right py-1.5 font-medium">Avg Chars</th>
                </tr>
              </thead>
              <tbody>
                {top_queries.map((q, i) => (
                  <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                    <td className="py-2 text-white/80 truncate max-w-xs" title={q.query}>
                      {q.query}
                    </td>
                    <td className="py-2 text-right text-white/60">{q.count}</td>
                    <td className="py-2 text-right text-white/40">
                      {fmtChars(q.avg_chars)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Recent turns */}
        <div className="bg-[#111113] border border-white/[0.06] rounded-xl p-5" data-testid="recall-recent-turns">
          <div className="text-white/60 text-xs uppercase tracking-wider mb-3">
            Recent Turns (last 20)
          </div>
          {recent_turns.length === 0 ? (
            <p className="text-white/30 text-sm">No turns.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#111113]">
                  <tr className="text-white/40 border-b border-white/5">
                    <th className="text-left py-1.5 font-medium">When</th>
                    <th className="text-left py-1.5 font-medium">Verdict</th>
                    <th className="text-right py-1.5 font-medium">T/C</th>
                    <th className="text-right py-1.5 font-medium">Chars</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_turns.map((t, i) => {
                    const when = t.created_at
                      ? new Date(t.created_at).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—";
                    const verdictColor =
                      t.verdict === "recall"
                        ? t.prefix_chars > 0
                          ? "text-emerald-400"
                          : t.drop_reasons?.length
                            ? "text-amber-400"
                            : "text-white/40"
                        : "text-white/30";
                    const verdictLabel =
                      t.verdict === "recall"
                        ? t.prefix_chars > 0
                          ? "injected"
                          : t.drop_reasons?.length
                            ? "filtered"
                            : "empty"
                        : "skip";
                    return (
                      <tr key={i} className="border-b border-white/[0.03]">
                        <td className="py-2 text-white/60 whitespace-nowrap">{when}</td>
                        <td className={`py-2 ${verdictColor} whitespace-nowrap`}>
                          {verdictLabel}
                          {t.cache_hit && (
                            <span className="ml-1 text-[9px] text-white/30">(c)</span>
                          )}
                        </td>
                        <td className="py-2 text-right text-white/60">
                          {t.timeline_titles_count}/{t.claim_ids_count}
                        </td>
                        <td className="py-2 text-right text-white/40">
                          {fmtChars(t.prefix_chars)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
